import { useState, useMemo } from "react";
import {
  Calendar,
  MapPin,
  Users,
  ChevronDown,
  Grid3x3,
  List,
  Filter,
  ChevronLeft,
  ChevronRight,
  CalendarX,
  Search,
} from "lucide-react";
import { Link } from "react-router-dom";
import Header from "../components/user/Header";
import Footer from "../components/user/Footer";
import FloatingActions from "../components/user/FloatingActions";





// SEO Metadata
export const metadata = {
  title: "Events Gallery | St. Stephen's Rentals & Events",
  description:
    "Explore our diverse collection of successful events. From weddings to corporate events, see how we bring visions to life in Lagos, Nigeria.",
  keywords:
    "events Lagos, wedding events, corporate events, birthday parties, event rentals Lagos",
  openGraph: {
    title: "Events Gallery | St. Stephen's Rentals & Events",
    description: "Explore our diverse collection of successful events",
    type: "website",
  },
};

export default function EventsPage() {
  const [selectedCategory, setSelectedCategory] = useState("All Events");
  const [viewMode, setViewMode] = useState("grid");
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 20;

  const categories = [
    "All Events",
    "Weddings",
    "Corporate",
    "Birthday Parties",
    "Graduations",
    "Religious Events",
  ];

  const events = [];

  //   const events = [
  //     {
  //       title: "Elegant Garden Wedding",
  //       category: "Weddings",
  //       date: "March 15, 2026",
  //       location: "Victoria Island, Lagos",
  //       guests: "200 Guests",
  //       image:
  //         "https://images.unsplash.com/photo-1519741497674-611481863552?w=800&h=600&fit=crop",
  //       description:
  //         "A stunning outdoor wedding celebration featuring luxurious white linens, crystal chandeliers, and romantic floral centerpieces.",
  //       featured: true,
  //     },
  //     {
  //       title: "Corporate Gala Dinner",
  //       category: "Corporate",
  //       date: "February 28, 2026",
  //       location: "Ikoyi, Lagos",
  //       guests: "500 Guests",
  //       image:
  //         "https://images.unsplash.com/photo-1511578314322-379afb476865?w=800&h=600&fit=crop",
  //       description:
  //         "Professional corporate event with elegant table settings, stage lighting, and premium audiovisual equipment.",
  //       featured: true,
  //     },
  //     {
  //       title: "50th Birthday Celebration",
  //       category: "Birthday Parties",
  //       date: "April 10, 2026",
  //       location: "Lekki, Lagos",
  //       guests: "150 Guests",
  //       image:
  //         "https://images.unsplash.com/photo-1464366400600-7168b8af9bc3?w=800&h=600&fit=crop",
  //       description:
  //         "Vibrant milestone birthday party with themed décor, cocktail furniture, and stunning backdrop installations.",
  //       featured: false,
  //     },
  //     {
  //       title: "University Graduation Ceremony",
  //       category: "Graduations",
  //       date: "May 20, 2026",
  //       location: "Yaba, Lagos",
  //       guests: "1000 Guests",
  //       image:
  //         "https://images.unsplash.com/photo-1523050854058-8df90110c9f1?w=800&h=600&fit=crop",
  //       description:
  //         "Large-scale graduation event with professional staging, seating arrangements, and complete event infrastructure.",
  //       featured: false,
  //     },
  //     {
  //       title: "Church Anniversary Celebration",
  //       category: "Religious Events",
  //       date: "June 5, 2026",
  //       location: "Surulere, Lagos",
  //       guests: "800 Guests",
  //       image:
  //         "https://images.unsplash.com/photo-1438032005730-c779502df39b?w=800&h=600&fit=crop",
  //       description:
  //         "Grand religious celebration with elegant canopy, sophisticated sound system, and beautiful floral arrangements.",
  //       featured: true,
  //     },
  //     {
  //       title: "Product Launch Event",
  //       category: "Corporate",
  //       date: "March 25, 2026",
  //       location: "Victoria Island, Lagos",
  //       guests: "300 Guests",
  //       image:
  //         "https://images.unsplash.com/photo-1505373877841-8d25f7d46678?w=800&h=600&fit=crop",
  //       description:
  //         "Modern product launch with LED lighting, contemporary furniture, branded backdrops, and interactive displays.",
  //       featured: false,
  //     },
  //   ];

  // Memoized filtered events for performance
  const filteredEvents = useMemo(() => {
    return selectedCategory === "All Events"
      ? events
      : events.filter((event) => event.category === selectedCategory);
  }, [selectedCategory]);

  // Memoized pagination calculations
  const { totalPages, startIndex, endIndex, paginatedEvents } = useMemo(() => {
    const total = Math.ceil(filteredEvents.length / itemsPerPage);
    const start = (currentPage - 1) * itemsPerPage;
    const end = start + itemsPerPage;
    const paginated = filteredEvents.slice(start, end);
    return {
      totalPages: total,
      startIndex: start,
      endIndex: end,
      paginatedEvents: paginated,
    };
  }, [filteredEvents, currentPage, itemsPerPage]);

  // Reset to page 1 when category changes
  const handleCategoryChange = (category) => {
    setSelectedCategory(category);
    setCurrentPage(1);
  };

  // Empty State Component
  const EmptyState = () => (
    <div className="flex flex-col items-center justify-center py-20 md:py-32 px-6">
      <div className="w-24 h-24 bg-[#F5F3FF] dark:bg-[#2A2440] rounded-full flex items-center justify-center mb-6">
        <CalendarX size={48} className="text-[#62397f] dark:text-[#A78BFA]" />
      </div>
      <h2 className="text-2xl md:text-3xl font-bold text-black dark:text-white mb-3">
        {selectedCategory === "All Events"
          ? "No Events Yet"
          : `No ${selectedCategory} Found`}
      </h2>
      <p className="text-[#666666] dark:text-[#A0A0A0] text-center max-w-md mb-8 leading-relaxed">
        {selectedCategory === "All Events"
          ? "We're currently curating our event gallery. Check back soon to see our amazing work!"
          : `We don't have any ${selectedCategory.toLowerCase()} in our gallery yet. Browse other categories or contact us to discuss your event.`}
      </p>
      <div className="flex flex-col sm:flex-row gap-3">
        {selectedCategory !== "All Events" && (
          <button
            onClick={() => handleCategoryChange("All Events")}
            className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-[#62397f] hover:bg-[#7C3AED] text-white font-semibold rounded-xl transition-all duration-200 transform hover:scale-[1.02] active:scale-[0.98] shadow-[0_4px_12px_rgba(139,92,246,0.3)]"
          >
            <Search size={18} />
            View All Events
          </button>
        )}
        <Link
          to="/contact"
          className="inline-flex items-center justify-center px-6 py-3 bg-[#F5F3FF] dark:bg-[#2A2440] hover:bg-[#EDE9FE] dark:hover:bg-[#342A50] text-[#62397f] dark:text-[#A78BFA] font-semibold rounded-xl transition-all duration-200 transform hover:scale-[1.02] active:scale-[0.98]"
        >
          Contact Us
        </Link>
      </div>
    </div>
  );

  return (
    <div
      className="min-h-screen bg-white dark:bg-[#121212]"
    >
      <Header />

      {/* Hero Section */}
      <section
        className="relative h-[400px] md:h-[500px] overflow-hidden bg-black"
        aria-label="Events hero section"
      >
        <img
          src="https://images.unsplash.com/photo-1519225421980-715cb0215aed?ixlib=rb-4.0.3&auto=format&fit=crop&w=2500&q=80"
          alt="Elegant event venue with professional setup and beautiful lighting"
          className="w-full h-full object-cover"
          loading="eager"
          fetchpriority="high"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/60 to-transparent"></div>
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent"></div>
        <div className="absolute inset-0 bg-[#62397f]/10"></div>

        <div className="relative h-full max-w-[1400px] mx-auto px-6 flex items-center justify-center">
          <div className="text-center">
            <h1
              className="text-white font-medium font-serif leading-[1.1] mb-4"
              style={{
                fontSize: "clamp(36px, 6vw, 72px)",
              }}
            >
              St. Stephen's Events
            </h1>
            <p className="text-white/90 text-lg md:text-xl max-w-2xl mx-auto leading-relaxed">
              Explore our diverse collection of successful events. From intimate
              gatherings to grand celebrations, see how we bring visions to
              life.
            </p>
          </div>
        </div>
      </section>

      {/* Filter Section */}
      <section
        className="sticky top-[73px] md:top-[77px] z-40 bg-white dark:bg-[#1E1E1E] border-b border-[#E9E9E9] dark:border-[#333333] shadow-sm"
        aria-label="Event filters"
      >
        <div className="max-w-[1400px] mx-auto px-6 py-4">
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
            {/* Categories - Desktop */}
            <div
              className="hidden md:flex items-center gap-3 flex-wrap"
              role="tablist"
              aria-label="Event categories"
            >
              {categories.map((category) => (
                <button
                  key={category}
                  onClick={() => handleCategoryChange(category)}
                  role="tab"
                  aria-selected={selectedCategory === category}
                  aria-label={`Filter by ${category}`}
                  className={`px-4 py-2 rounded-full text-sm font-semibold transition-all duration-200 ${selectedCategory === category
                    ? "bg-[#62397f] text-white shadow-[0_4px_12px_rgba(139,92,246,0.3)]"
                    : "bg-[#F5F3FF] dark:bg-[#2A2440] text-[#62397f] dark:text-[#A78BFA] hover:bg-[#EDE9FE] dark:hover:bg-[#342A50]"
                    }`}
                >
                  {category}
                </button>
              ))}
            </div>

            {/* Mobile Filter Dropdown */}
            <div className="md:hidden w-full">
              <button
                onClick={() => setIsFilterOpen(!isFilterOpen)}
                aria-expanded={isFilterOpen}
                aria-label="Open category filter"
                className="w-full flex items-center justify-between px-4 py-3 bg-[#F5F3FF] dark:bg-[#2A2440] text-[#62397f] dark:text-[#A78BFA] rounded-xl font-semibold"
              >
                <div className="flex items-center gap-2">
                  <Filter size={18} />
                  <span>{selectedCategory}</span>
                </div>
                <ChevronDown
                  size={18}
                  className={`transition-transform duration-200 ${isFilterOpen ? "rotate-180" : ""
                    }`}
                />
              </button>

              {isFilterOpen && (
                <div
                  className="mt-2 bg-white dark:bg-[#1E1E1E] border border-[#E9E9E9] dark:border-[#333333] rounded-xl shadow-lg overflow-hidden"
                  role="menu"
                >
                  {categories.map((category) => (
                    <button
                      key={category}
                      onClick={() => {
                        handleCategoryChange(category);
                        setIsFilterOpen(false);
                      }}
                      role="menuitem"
                      className={`w-full text-left px-4 py-3 text-sm font-medium transition-colors duration-150 ${selectedCategory === category
                        ? "bg-[#F5F3FF] dark:bg-[#2A2440] text-[#62397f] dark:text-[#A78BFA]"
                        : "text-[#333333] dark:text-[#E0E0E0] hover:bg-[#F5F3FF] dark:hover:bg-[#2A2440]"
                        }`}
                    >
                      {category}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* View Toggle */}
            <div
              className="flex items-center gap-2 bg-[#F5F3FF] dark:bg-[#2A2440] rounded-full p-1"
              role="group"
              aria-label="View mode"
            >
              <button
                onClick={() => setViewMode("grid")}
                className={`p-2 rounded-full transition-all duration-200 ${viewMode === "grid"
                  ? "bg-[#62397f] text-white shadow-sm"
                  : "text-[#62397f] dark:text-[#A78BFA] hover:bg-[#EDE9FE] dark:hover:bg-[#342A50]"
                  }`}
                aria-label="Grid view"
                aria-pressed={viewMode === "grid"}
              >
                <Grid3x3 size={18} />
              </button>
              <button
                onClick={() => setViewMode("list")}
                className={`p-2 rounded-full transition-all duration-200 ${viewMode === "list"
                  ? "bg-[#62397f] text-white shadow-sm"
                  : "text-[#62397f] dark:text-[#A78BFA] hover:bg-[#EDE9FE] dark:hover:bg-[#342A50]"
                  }`}
                aria-label="List view"
                aria-pressed={viewMode === "list"}
              >
                <List size={18} />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Events Grid/List or Empty State */}
      <section className="py-12 md:py-20 px-6" aria-label="Events list">
        <div className="max-w-[1400px] mx-auto">
          {filteredEvents.length === 0 ? (
            <EmptyState />
          ) : (
            <>
              {/* Results Count */}
              <div className="mb-8">
                <p
                  className="text-[#666666] dark:text-[#A0A0A0] text-sm"
                  role="status"
                  aria-live="polite"
                >
                  Showing {startIndex + 1}-
                  {Math.min(endIndex, filteredEvents.length)} of{" "}
                  {filteredEvents.length}{" "}
                  {filteredEvents.length === 1 ? "event" : "events"}
                </p>
              </div>

              {/* Grid View */}
              {viewMode === "grid" && (
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6 md:gap-8">
                  {paginatedEvents.map((event, index) => (
                    <article
                      key={`${event.title}-${index}`}
                      className="group bg-white dark:bg-[#1E1E1E] rounded-2xl overflow-hidden border border-[#E9E9E9] dark:border-[#333333] transition-all duration-300 hover:border-[#62397f] dark:hover:border-[#A78BFA] hover:shadow-[0_12px_32px_rgba(139,92,246,0.15)] dark:hover:shadow-[0_12px_32px_rgba(0,0,0,0.3)] transform hover:-translate-y-2"
                    >
                      {/* Image */}
                      <div className="relative h-64 overflow-hidden bg-[#F5F5F5] dark:bg-[#2A2A2A]">
                        <img
                          src={event.image}
                          alt={`${event.title} - ${event.description.substring(0, 100)}`}
                          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                          loading="lazy"
                          decoding="async"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent"></div>
                        {event.featured && (
                          <div className="absolute top-4 left-4 px-3 py-1 bg-[#62397f] text-white text-xs font-bold uppercase tracking-wider rounded-full">
                            Featured
                          </div>
                        )}
                        <div className="absolute top-4 right-4 px-3 py-1.5 bg-white/90 dark:bg-black/80 backdrop-blur-sm rounded-full text-xs font-bold text-[#62397f] dark:text-[#A78BFA]">
                          {event.category}
                        </div>
                      </div>

                      {/* Content */}
                      <div className="p-6">
                        <h3 className="text-xl font-bold text-black dark:text-white mb-3 group-hover:text-[#62397f] dark:group-hover:text-[#A78BFA] transition-colors duration-200">
                          {event.title}
                        </h3>
                        <p className="text-[#666666] dark:text-[#A0A0A0] text-sm leading-relaxed mb-4">
                          {event.description}
                        </p>

                        {/* Event Details */}
                        <div className="space-y-2 mb-4">
                          <div className="flex items-center gap-2 text-sm text-[#666666] dark:text-[#A0A0A0]">
                            <Calendar
                              size={16}
                              className="text-[#62397f]"
                              aria-hidden="true"
                            />
                            <time dateTime={event.date}>{event.date}</time>
                          </div>
                          <div className="flex items-center gap-2 text-sm text-[#666666] dark:text-[#A0A0A0]">
                            <MapPin
                              size={16}
                              className="text-[#62397f]"
                              aria-hidden="true"
                            />
                            <span>{event.location}</span>
                          </div>
                          <div className="flex items-center gap-2 text-sm text-[#666666] dark:text-[#A0A0A0]">
                            <Users
                              size={16}
                              className="text-[#62397f]"
                              aria-hidden="true"
                            />
                            <span>{event.guests}</span>
                          </div>
                        </div>

                        {/* CTA */}
                        <button
                          className="w-full px-4 py-3 bg-[#F5F3FF] dark:bg-[#2A2440] text-[#62397f] dark:text-[#A78BFA] font-semibold rounded-xl hover:bg-[#62397f] hover:text-white dark:hover:bg-[#62397f] transition-all duration-200"
                          aria-label={`View details for ${event.title}`}
                        >
                          View Details
                        </button>
                      </div>
                    </article>
                  ))}
                </div>
              )}

              {/* List View */}
              {viewMode === "list" && (
                <div className="space-y-6">
                  {paginatedEvents.map((event, index) => (
                    <article
                      key={`${event.title}-${index}`}
                      className="group bg-white dark:bg-[#1E1E1E] rounded-2xl overflow-hidden border border-[#E9E9E9] dark:border-[#333333] transition-all duration-300 hover:border-[#62397f] dark:hover:border-[#A78BFA] hover:shadow-[0_12px_32px_rgba(139,92,246,0.15)] dark:hover:shadow-[0_12px_32px_rgba(0,0,0,0.3)]"
                    >
                      <div className="flex flex-col md:flex-row">
                        {/* Image */}
                        <div className="relative w-full md:w-80 h-64 md:h-auto overflow-hidden bg-[#F5F5F5] dark:bg-[#2A2A2A]">
                          <img
                            src={event.image}
                            alt={`${event.title} - ${event.description.substring(0, 100)}`}
                            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                            loading="lazy"
                            decoding="async"
                          />
                          <div className="absolute inset-0 bg-gradient-to-t md:bg-gradient-to-r from-black/60 via-black/20 to-transparent"></div>
                          {event.featured && (
                            <div className="absolute top-4 left-4 px-3 py-1 bg-[#62397f] text-white text-xs font-bold uppercase tracking-wider rounded-full">
                              Featured
                            </div>
                          )}
                        </div>

                        {/* Content */}
                        <div className="flex-1 p-6 md:p-8">
                          <div className="flex items-start justify-between mb-4">
                            <div>
                              <div className="inline-block px-3 py-1 bg-[#F5F3FF] dark:bg-[#2A2440] text-[#62397f] dark:text-[#A78BFA] text-xs font-bold uppercase tracking-wider rounded-full mb-3">
                                {event.category}
                              </div>
                              <h3 className="text-2xl font-bold text-black dark:text-white mb-2 group-hover:text-[#62397f] dark:group-hover:text-[#A78BFA] transition-colors duration-200">
                                {event.title}
                              </h3>
                            </div>
                          </div>

                          <p className="text-[#666666] dark:text-[#A0A0A0] leading-relaxed mb-6">
                            {event.description}
                          </p>

                          <div className="flex flex-wrap gap-6 mb-6">
                            <div className="flex items-center gap-2 text-sm text-[#666666] dark:text-[#A0A0A0]">
                              <Calendar
                                size={16}
                                className="text-[#62397f]"
                                aria-hidden="true"
                              />
                              <time dateTime={event.date}>{event.date}</time>
                            </div>
                            <div className="flex items-center gap-2 text-sm text-[#666666] dark:text-[#A0A0A0]">
                              <MapPin
                                size={16}
                                className="text-[#62397f]"
                                aria-hidden="true"
                              />
                              <span>{event.location}</span>
                            </div>
                            <div className="flex items-center gap-2 text-sm text-[#666666] dark:text-[#A0A0A0]">
                              <Users
                                size={16}
                                className="text-[#62397f]"
                                aria-hidden="true"
                              />
                              <span>{event.guests}</span>
                            </div>
                          </div>

                          <button
                            className="px-6 py-3 bg-[#F5F3FF] dark:bg-[#2A2440] text-[#62397f] dark:text-[#A78BFA] font-semibold rounded-xl hover:bg-[#62397f] hover:text-white dark:hover:bg-[#62397f] transition-all duration-200"
                            aria-label={`View details for ${event.title}`}
                          >
                            View Details
                          </button>
                        </div>
                      </div>
                    </article>
                  ))}
                </div>
              )}

              {/* Pagination */}
              {totalPages > 1 && (
                <nav
                  className="mt-12 flex items-center justify-center gap-2"
                  aria-label="Pagination navigation"
                >
                  <button
                    onClick={() =>
                      setCurrentPage((prev) => Math.max(prev - 1, 1))
                    }
                    disabled={currentPage === 1}
                    className="p-2 rounded-lg bg-[#F5F3FF] dark:bg-[#2A2440] text-[#62397f] dark:text-[#A78BFA] hover:bg-[#EDE9FE] dark:hover:bg-[#342A50] disabled:opacity-40 disabled:cursor-not-allowed transition-all duration-200"
                    aria-label="Go to previous page"
                  >
                    <ChevronLeft size={20} />
                  </button>

                  <div className="flex items-center gap-2">
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map(
                      (page) => (
                        <button
                          key={page}
                          onClick={() => setCurrentPage(page)}
                          className={`min-w-[40px] h-10 px-3 rounded-lg font-semibold transition-all duration-200 ${currentPage === page
                            ? "bg-[#62397f] text-white shadow-[0_4px_12px_rgba(139,92,246,0.3)]"
                            : "bg-[#F5F3FF] dark:bg-[#2A2440] text-[#62397f] dark:text-[#A78BFA] hover:bg-[#EDE9FE] dark:hover:bg-[#342A50]"
                            }`}
                          aria-label={`Go to page ${page}`}
                          aria-current={
                            currentPage === page ? "page" : undefined
                          }
                        >
                          {page}
                        </button>
                      ),
                    )}
                  </div>

                  <button
                    onClick={() =>
                      setCurrentPage((prev) => Math.min(prev + 1, totalPages))
                    }
                    disabled={currentPage === totalPages}
                    className="p-2 rounded-lg bg-[#F5F3FF] dark:bg-[#2A2440] text-[#62397f] dark:text-[#A78BFA] hover:bg-[#EDE9FE] dark:hover:bg-[#342A50] disabled:opacity-40 disabled:cursor-not-allowed transition-all duration-200"
                    aria-label="Go to next page"
                  >
                    <ChevronRight size={20} />
                  </button>
                </nav>
              )}
            </>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 md:py-28 px-6 bg-gradient-to-br from-[#62397f] via-[#7C3AED] to-[#6D28D9] relative overflow-hidden">
        <div
          className="absolute top-0 right-0 w-96 h-96 bg-white/5 rounded-full blur-3xl"
          aria-hidden="true"
        ></div>
        <div
          className="absolute bottom-0 left-0 w-80 h-80 bg-black/10 rounded-full blur-3xl"
          aria-hidden="true"
        ></div>

        <div className="relative max-w-[1400px] mx-auto text-center">
          <h2
            className="text-white font-medium font-serif leading-tight mb-4 md:mb-6"
            style={{
              fontSize: "clamp(32px, 5vw, 48px)",
            }}
          >
            Ready to Plan Your Event?
          </h2>
          <p className="text-white/90 text-lg md:text-xl mb-10 max-w-2xl mx-auto">
            Let our experienced team help you create an unforgettable
            celebration
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact"
              className="inline-flex items-center justify-center px-8 py-4 bg-white hover:bg-gray-50 text-[#62397f] font-semibold rounded-full transition-all duration-200 transform hover:scale-[1.02] active:scale-[0.98] shadow-[0_8px_24px_rgba(0,0,0,0.15)]"
            >
              Get in Touch
            </Link>
            <Link
              to="/rentals"
              className="inline-flex items-center justify-center px-8 py-4 bg-white/10 hover:bg-white/20 text-white font-semibold rounded-full border-2 border-white/30 backdrop-blur-sm transition-all duration-200 transform hover:scale-[1.02] active:scale-[0.98]"
            >
              Browse Rentals
            </Link>
          </div>
        </div>
      </section>

      <Footer />
      <FloatingActions />
    </div>
  );
}




